import { AdminNav } from "@bcc/components/admin/admin-nav";
import { AdminProductsView } from "@bcc/components/views/admin-products-view";
import { getAdminCatalogue as getLiveAdminCatalogue } from "@client-shared/services/api/product";
import type { Product } from "@shared/types/product";
import { useQuery } from "@tanstack/react-query";
import { useAdminProductsRegion } from "@bcc/components/views/admin-products-url-state";
import { adminProductCatalogueQueryKey, DEFAULT_CATALOGUE_REGION, setAdminProductsPageTestId } from "./admin-products-page.utils";

// Stable while the query has no data, so the table doesn't see fresh rows on every render.
const NO_PRODUCTS: Product[] = [];

interface AdminProductsPageProps {
  /** Catalogue loader; defaults to the live API. Stories and tests inject fixtures here, never the page itself. */
  readonly fetchCatalogue?: typeof getLiveAdminCatalogue;
}

/**
 * Admin regional product catalogue.
 *
 * Scoped to the admin only: it never reads the selected vendor or installation,
 * so a manager can browse a region without impersonating a roaster.
 */
export function AdminProductsPage({ fetchCatalogue = getLiveAdminCatalogue }: AdminProductsPageProps) {
  const [region, setRegion] = useAdminProductsRegion(DEFAULT_CATALOGUE_REGION);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: adminProductCatalogueQueryKey(region),
    queryFn: ({ signal }) => fetchCatalogue({ region }, signal),
    // A 403 or a failed full-catalogue walk won't recover on its own; surface
    // the error straight away and let the user retry.
    retry: false,
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6" data-testid={setAdminProductsPageTestId("container")}>
      <AdminNav />
      <AdminProductsView
        products={data?.products ?? NO_PRODUCTS}
        region={region}
        onRegionChange={setRegion}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
      />
    </div>
  );
}
